import React from 'react';
import { Row, Col, Form, InputNumber, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { useMutation } from '@apollo/client';

import { getSymbolFromCurrency } from '~helpers/currency';
import { dayFormatType, moment, formatDay } from '~helpers/day';
import { MUTATION_UPDATE_RECONCILIATION_RENT } from '../../queries';

import Modal from 'common/components/modal';

const useStyles = makeStyles(() => ({
  wrapper: {
    '& .ant-modal-header': {
      padding: '32px 32px 24px 32px',
    },
    '& .ant-modal-body': {
      padding: '0px 32px 8px',
    },

    '& .ant-form-item-label': {
      lineHeight: '22px',
      marginBottom: 8,
    },
  },
}));

const EditBookingInformationModal = ({
  form,
  refetchData,
  orderInfo,
  visible = false,
  setVisible = () => {},
}) => {
  const { getFieldDecorator, validateFields } = form;
  const classes = useStyles();
  const { t } = useTranslation();

  const handleCancelModal = () => {
    setVisible(false);
  };

  const [updateReconciliationRent, { loading: updateRendLoading }] = useMutation(
    MUTATION_UPDATE_RECONCILIATION_RENT,
    {
      fetchPolicy: 'network-only',
      onCompleted: res => {
        if (res?.updateOrderRent?.order?.id) {
          refetchData?.();
          handleCancelModal();
        }
      },
    },
  );

  const handleConfirmClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const { moveInDate, moveOutDate, totalPrice } = values;
        const params = {
          id: orderInfo?.order?.id,
          moveInDate: formatDay(dayFormatType.FILTERPARAMS, moveInDate),
          moveOutDate: formatDay(dayFormatType.FILTERPARAMS, moveOutDate),
          totalPrice: String(totalPrice),
        };

        updateReconciliationRent({
          variables: {
            input: params,
          },
        });
      }
    });
  };

  return (
    <Modal
      width={446}
      visible={visible}
      title={t('cms.reconciliation.lead_generation.update.modal.booking.title')}
      okText={t('cms.reconciliation.lead_generation.update.modal.ok_btn')}
      wrapClassName={classes.wrapper}
      onCancel={handleCancelModal}
      onOk={handleConfirmClick}
      okButtonProps={{
        loading: updateRendLoading,
      }}
      destroyOnClose={true}
    >
      <Form>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item label={t('cms.reconciliation.lead_generation.update.modal.move_in')}>
              {getFieldDecorator('moveInDate', {
                initialValue: orderInfo?.order?.moveInDate
                  ? moment(orderInfo?.order?.moveInDate)
                  : undefined,
                rules: [
                  {
                    validator: (_, value, callback) => {
                      value
                        ? callback()
                        : callback(
                            t('cms.reconciliation.booking.details.edit.modal.datepicker.empty'),
                          );
                    },
                  },
                ],
              })(
                <DatePicker
                  placeholder={t(
                    'cms.reconciliation.booking.details.edit.modal.datepicker.placeholder',
                  )}
                  format={dayFormatType.UKFORMAT}
                  picker="month"
                  style={{ width: '100%' }}
                  inputReadOnly
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={t('cms.reconciliation.lead_generation.update.modal.move_out')}>
              {getFieldDecorator('moveOutDate', {
                initialValue: orderInfo?.order?.moveOutDate
                  ? moment(orderInfo?.order?.moveOutDate)
                  : undefined,
                rules: [
                  {
                    validator: (_, value, callback) => {
                      value
                        ? callback()
                        : callback(
                            t('cms.reconciliation.booking.details.edit.modal.datepicker.empty'),
                          );
                    },
                  },
                ],
              })(
                <DatePicker
                  placeholder={t(
                    'cms.reconciliation.booking.details.edit.modal.datepicker.placeholder',
                  )}
                  format={dayFormatType.UKFORMAT}
                  picker="month"
                  style={{ width: '100%' }}
                  inputReadOnly
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={t('cms.reconciliation.lead_generation.update.modal.total_rent')}>
              {getFieldDecorator('totalPrice', {
                initialValue: orderInfo?.order?.totalRental ?? 0,
                rules: [
                  {
                    type: 'number',
                    validator: (_, value, callback) => {
                      value
                        ? callback()
                        : callback(
                            t('cms.reconciliation.lead_generation.update.modal.total_rent.error'),
                          );
                    },
                  },
                ],
              })(
                <InputNumber
                  formatter={value =>
                    value
                      ? `${getSymbolFromCurrency(orderInfo?.order?.currency)} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ',',
                        )
                      : undefined
                  }
                  parser={value => value.replace(/[^0-9.]/g, '')}
                  min={0.01}
                  precision={2}
                  placeholder={t(
                    'cms.reconciliation.lead_generation.update.modal.total_rent.error',
                  )}
                />,
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Form.create({
  name: 'edit-booking-information-modal',
})(EditBookingInformationModal);
